import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import ParentLayout from '../components/ParentLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { PlayerAvatar, UserAvatar } from '../components/ui/Avatar';
import AdminLinkGuardianModal from '../components/AdminLinkGuardianModal';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, User, MapPin, HeartPulse, CheckCircle, XCircle, AlertTriangle,
  Mail, Phone, FileText, Award, Users, ArrowUpRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Same display map used by AdminRegistrationsList for graceful division fallback
const DIVISION_DISPLAY = {
  'flag_little_chiefs': 'Flag Football — Little Chiefs (5–7U)',
  '5-7U': 'Flag Football — Little Chiefs (5–7U)',
  '5U': 'Flag Football — Little Chiefs (5–7U)',
  '6U': 'Flag Football — Little Chiefs (5–7U)',
  'div_5_6u': 'Flag Football — Little Chiefs (5–7U)',
  'div_little_chiefs': 'Flag Football — Little Chiefs (5–7U)',
  '7-8U': 'Tackle Football — Tiny Chiefs (7–8U)',
  'div_7_8u': 'Tackle Football — Tiny Chiefs (7–8U)',
  '9U': 'Tackle Football — Mighty Chiefs (9U)',
  'div_9u': 'Tackle Football — Mighty Chiefs (9U)',
  '10U': 'Tackle Football — Junior Chiefs (10U)',
  'div_10u': 'Tackle Football — Junior Chiefs (10U)',
  '11U': 'Tackle Football — Varsity Chiefs (11U)',
  'div_11u': 'Tackle Football — Varsity Chiefs (11U)',
  '12U': 'Tackle Football — Senior Chiefs (12U)',
  'div_12u': 'Tackle Football — Senior Chiefs (12U)',
  '13U': 'Tackle Football — Elite Chiefs (13U)',
  'div_13u': 'Tackle Football — Elite Chiefs (13U)',
  'cheer_sparks': 'Cheer — Lady Chiefs Sparks (5–8)',
  'cheer_stars': 'Cheer — Lady Chiefs Stars (9–11)',
  'cheer_elite': 'Cheer — Lady Chiefs Elite (12–14)'
};

const PROGRAM_LABEL = {
  flag_football: 'Flag Football',
  tackle_football: 'Tackle Football',
  cheer: 'Cheer'
};

const STATUS_BADGE = {
  'not_started': { label: 'Not Started', className: 'bg-gray-100 text-gray-800' },
  'in_progress': { label: 'In Progress', className: 'bg-blue-100 text-blue-800' },
  'submitted': { label: 'Submitted', className: 'bg-purple-100 text-purple-800' },
  'pending_review': { label: 'Pending Review', className: 'bg-yellow-100 text-yellow-800' },
  'incomplete': { label: 'Incomplete', className: 'bg-orange-100 text-orange-800' },
  'approved': { label: 'Approved', className: 'bg-green-100 text-green-800' },
  'denied': { label: 'Denied', className: 'bg-red-100 text-red-800' },
  'waitlisted': { label: 'Waitlisted', className: 'bg-indigo-100 text-indigo-800' },
  'archived': { label: 'Archived', className: 'bg-gray-200 text-gray-600' }
};

const PAYMENT_BADGE = {
  'not_started': { label: 'Not Started', className: 'bg-gray-100 text-gray-800' },
  'pending': { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
  'submitted': { label: 'Submitted', className: 'bg-blue-100 text-blue-800' },
  'confirmed': { label: 'Confirmed', className: 'bg-green-100 text-green-800' },
  'failed': { label: 'Rejected', className: 'bg-red-100 text-red-800' },
  'waived': { label: 'Waived', className: 'bg-purple-100 text-purple-800' },
  'paid': { label: 'Paid', className: 'bg-green-100 text-green-800' }
};

const WAIVER_DEFS = [
  { type: 'participation', label: 'Participation & Liability' },
  { type: 'medical_consent', label: 'Emergency Medical Treatment Consent' },
  { type: 'code_of_conduct', label: 'Code of Conduct' }
];
const DOC_DEFS = [
  { type: 'birth_certificate', label: 'Birth Certificate' },
  { type: 'report_card', label: 'Report Card' },
  { type: 'medical_consent_form', label: 'Medical Consent Form' }
];

// Parse "YYYY-MM-DD" (or full-ISO with 'T' suffix) as a LOCAL-time calendar
// date — never UTC — to avoid timezone-driven day shifting on display.
// `new Date("YYYY-MM-DD")` is parsed by the JS spec as UTC midnight, which
// renders as the previous calendar day in any timezone west of UTC.
const parseLocalDate = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const calcAge = (dob) => {
  if (!dob) return null;
  const d = parseLocalDate(dob);
  if (!d || Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 100 ? age : null;
};

const formatAddress = (household) => {
  if (!household) return '';
  const a = household.address;
  let parts;
  if (a && typeof a === 'object') {
    parts = [a.street, a.city || household.city, a.state || household.state, a.zip || household.zip];
  } else {
    parts = [a, household.city, household.state, household.zip];
  }
  return parts.filter(Boolean).join(', ');
};

const Em = ({ children }) => (children === null || children === undefined || children === '' ? <span className="text-gray-400">—</span> : children);

// Safely coerce any FastAPI 422 / axios error detail shape into a display string.
const errorToString = (detail, fallback = 'Something went wrong') => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((d) => (d && d.msg) ? d.msg : (typeof d === 'string' ? d : ''));
    const joined = parts.filter(Boolean).join(', ');
    return joined || fallback;
  }
  if (typeof detail === 'object' && detail.msg) return String(detail.msg);
  return fallback;
};

export default function AdminPlayerReportPage() {
  const { playerId } = useParams();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Assign / Move modal state (super_admin only).
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignOptions, setAssignOptions] = useState(null);
  const [assignChoiceTeamId, setAssignChoiceTeamId] = useState('');
  const [assignReason, setAssignReason] = useState('');
  const [assignError, setAssignError] = useState('');
  const [assignSuccess, setAssignSuccess] = useState('');
  // Aug 2026: admin guardian linking modal (link/invite).
  const [linkGuardianOpen, setLinkGuardianOpen] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/admin/players/${playerId}/quick-review`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      setError(errorToString(err.response?.data?.detail, 'Failed to load player report'));
    } finally {
      setLoading(false);
    }
  };

  const openAssignModal = async () => {
    setAssignOpen(true);
    setAssignLoading(true);
    setAssignError('');
    setAssignSuccess('');
    setAssignChoiceTeamId('');
    setAssignReason('');
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/players/${playerId}/assignment-options`,
        { withCredentials: true }
      );
      setAssignOptions(res.data);
    } catch (err) {
      setAssignError(errorToString(err.response?.data?.detail, 'Failed to load assignment options'));
      setAssignOptions(null);
    } finally {
      setAssignLoading(false);
    }
  };

  const submitAssignment = async () => {
    if (!assignChoiceTeamId) {
      setAssignError('Please select a destination team.');
      return;
    }
    const allTeams = [
      ...(assignOptions?.same_division_teams || []),
      ...(assignOptions?.play_up_teams || []),
      ...((assignOptions?.all_teams_by_division || []).flatMap(g => g.teams || [])),
    ];
    const chosen = allTeams.find(t => t.team_id === assignChoiceTeamId);
    if (!chosen) {
      setAssignError('Selected team is no longer available.');
      return;
    }
    if (chosen.is_current_team) {
      setAssignError('Player is already on this team.');
      return;
    }
    if (chosen.requires_play_up && !assignReason.trim()) {
      setAssignError('A reason is required when playing up one level.');
      return;
    }
    if (chosen.requires_manual_override && !assignReason.trim()) {
      setAssignError('A reason is required for a manual override to a division outside the true / stored / play-up buckets.');
      return;
    }
    setAssignSubmitting(true);
    setAssignError('');
    setAssignSuccess('');
    try {
      const currentTeamId = assignOptions?.player?.current_team_id;
      const body = {};
      if (chosen.requires_manual_override) {
        body.manual_override = true;
        body.override_reason = assignReason.trim();
      } else if (chosen.requires_play_up) {
        body.play_up_one_level = true;
        if (assignReason.trim()) body.play_up_reason = assignReason.trim();
      }
      if (currentTeamId) {
        // Already rostered → /roster/move (query params + optional play-up body)
        await axios.post(
          `${API_URL}/api/roster/move`,
          body,
          {
            withCredentials: true,
            params: { player_id: playerId, new_team_id: assignChoiceTeamId },
          }
        );
      } else {
        // Unrostered → /roster/assign (JSON body)
        await axios.post(
          `${API_URL}/api/roster/assign`,
          { player_id: playerId, team_id: assignChoiceTeamId, ...body },
          { withCredentials: true }
        );
      }
      const label = chosen.requires_manual_override
        ? `Player manually moved to "${chosen.team_name}" (override recorded).`
        : chosen.requires_play_up
          ? `Player moved with one-level play-up to "${chosen.team_name}".`
          : `Player assigned to "${chosen.team_name}".`;
      setAssignSuccess(label);
      // Refresh underlying report so UI reflects the new team immediately.
      await fetchReport();
      setTimeout(() => { setAssignOpen(false); }, 900);
    } catch (err) {
      setAssignError(errorToString(err.response?.data?.detail, 'Assignment failed'));
    } finally {
      setAssignSubmitting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_URL}/api/admin/players/${playerId}/quick-review`, { withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load player report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [playerId]);

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-6xl mx-auto px-4 py-8 text-center text-gray-500">Loading player report…</div>
      </ParentLayout>
    );
  }
  if (error || !data) {
    return (
      <ParentLayout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Link to="/admin/registrations" className="inline-flex items-center text-sm text-navy hover:underline" data-testid="admin-player-report-back-link">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Registrations
          </Link>
          <div className="mt-6 p-4 border rounded-lg bg-red-50 border-red-200 text-red-700">
            {error || 'Player not found'}
          </div>
        </div>
      </ParentLayout>
    );
  }

  const player = data.player || {};
  const reg = data.registration || {};
  const household = data.household || {};
  const parent = data.parent;
  const secondary = Array.isArray(data.secondary_parents) ? data.secondary_parents : [];
  const team = data.team;
  const divName = team?.division_name || data.division_name || (data.division_id ? DIVISION_DISPLAY[data.division_id] : null);
  const programLabel = PROGRAM_LABEL[data.program_type] || data.program_type || null;
  const status = STATUS_BADGE[reg.status] || { label: reg.status || 'Unknown', className: 'bg-gray-100 text-gray-800' };
  const payment = PAYMENT_BADGE[reg.payment_status] || { label: reg.payment_status || 'Unknown', className: 'bg-gray-100 text-gray-800' };
  const eligibility = player.eligibility_status || 'unknown';
  const eligClass = eligibility === 'eligible' ? 'bg-green-100 text-green-800'
    : eligibility === 'pending' || eligibility === 'pending_verification' ? 'bg-yellow-100 text-yellow-800'
    : 'bg-red-100 text-red-800';

  // Compliance: build waiver + document rows + missing list
  const waivers = Array.isArray(data.waivers) ? data.waivers : [];
  const documents = Array.isArray(data.documents) ? data.documents : [];
  const waiverRows = WAIVER_DEFS.map(d => {
    const w = waivers.find(x => x.waiver_type === d.type);
    return { ...d, signed: w?.status === 'signed', signed_at: w?.signed_at, signature: w?.signature };
  });
  const docRows = DOC_DEFS.map(d => {
    const doc = documents.find(x => x.document_type === d.type);
    return { ...d, uploaded: !!doc, filename: doc?.filename, uploaded_at: doc?.uploaded_at };
  });
  const waiversSigned = waiverRows.filter(w => w.signed).length;
  const docsUploaded = docRows.filter(d => d.uploaded).length;
  const missingItems = [
    ...waiverRows.filter(w => !w.signed).map(w => `${w.label} (waiver)`),
    ...docRows.filter(d => !d.uploaded).map(d => `${d.label} (document)`)
  ];

  const age = calcAge(player.date_of_birth);
  const fullAddress = formatAddress(household);

  const ParentBlock = ({ p, isPrimary }) => {
    const u = p?.user || {};
    const email = u.email;
    return (
      <div className="flex items-start space-x-3" data-testid={isPrimary ? 'admin-player-report-primary-parent' : 'admin-player-report-secondary-parent'}>
        <UserAvatar photoUrl={u.picture} name={u.name} size="md" />
        <div className="text-sm min-w-0 flex-1">
          {email ? (
            <Link to={`/admin/users?search=${encodeURIComponent(email)}`} className="font-semibold text-navy hover:underline" data-testid="admin-player-report-parent-link">
              <Em>{u.name || ''}</Em>
            </Link>
          ) : (
            <span className="font-semibold"><Em>{u.name || ''}</Em></span>
          )}
          <div className="text-gray-600 flex items-center mt-0.5">
            <Mail className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
            {email ? <a className="hover:underline" href={`mailto:${email}`}>{email}</a> : <Em />}
          </div>
          <div className="text-gray-600 flex items-center mt-0.5">
            <Phone className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
            {u.phone ? <a className="hover:underline" href={`tel:${u.phone}`}>{u.phone}</a> : <Em />}
          </div>
        </div>
      </div>
    );
  };

  return (
    <ParentLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="admin-player-report-page">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <Link to="/admin/registrations" className="inline-flex items-center text-sm text-navy hover:underline" data-testid="admin-player-report-back-link">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Registrations
          </Link>
          <div className="text-xs uppercase tracking-[0.16em] text-gold font-bold">Player Report</div>
        </div>

        {/* Identity + Program/Team grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {/* Identity */}
          <Card className="lg:col-span-2" data-testid="admin-player-report-identity">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-navy flex items-center"><User className="w-4 h-4 mr-2" /> Player Identity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-4 flex-wrap">
                <PlayerAvatar
                  photoUrl={player.photo_url}
                  firstName={player.first_name}
                  lastName={player.last_name}
                  size="2xl"
                />
                <div className="flex-1 min-w-[220px]">
                  <h1 className="text-2xl font-bold text-navy"><Em>{`${player.first_name || ''} ${player.last_name || ''}`.trim()}</Em></h1>
                  <p className="text-sm text-gray-500 mt-0.5">Player ID: {player.player_id || '—'}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mt-4 text-sm">
                    <div><span className="text-gray-500">DOB:</span> <Em>{parseLocalDate(player.date_of_birth)?.toLocaleDateString() ?? ''}</Em></div>
                    <div><span className="text-gray-500">Age:</span> <Em>{age != null ? `${age} yrs` : ''}</Em></div>
                    <div data-testid="season-age-chip">
                      <span className="text-gray-500">Season age:</span>{' '}
                      <Em>{data.season_age_group ? `${data.season_age_group}` : ''}</Em>
                      {data.season_year ? <span className="text-gray-400 text-xs ml-1">({data.season_year})</span> : null}
                    </div>
                    <div><span className="text-gray-500">Grade:</span> <Em>{player.grade || ''}</Em></div>
                    <div><span className="text-gray-500">School:</span> <Em>{player.school || ''}</Em></div>
                    <div><span className="text-gray-500">Position:</span> <Em>{player.position || ''}</Em></div>
                    <div><span className="text-gray-500">Jersey:</span> <Em>{player.jersey_number ? `#${player.jersey_number}${player.jersey_size ? ` (${player.jersey_size})` : ''}` : ''}</Em></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Program / Team */}
          <Card data-testid="admin-player-report-program-team">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-navy flex items-center"><Award className="w-4 h-4 mr-2" /> Program &amp; Team</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div><span className="text-gray-500">Program:</span> <Em>{programLabel}</Em></div>
              <div><span className="text-gray-500">Division:</span> <Em>{divName}</Em></div>
              <div><span className="text-gray-500">Team:</span> <Em>{team?.name}</Em></div>
              <div className="flex items-center justify-between border-t pt-2 mt-2">
                <span className="text-gray-500">Roster:</span>
                <Badge className={team?.name ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}>
                  {team?.name ? 'Assigned' : 'Not assigned'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Eligibility:</span>
                <Badge className={eligClass}>{eligibility}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Status:</span>
                <Badge className={status.className}>{status.label}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Payment:</span>
                <Badge className={payment.className}>{payment.label}</Badge>
              </div>
              {isSuperAdmin && (
                <div className="pt-2 border-t mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full border-navy text-navy hover:bg-navy hover:text-white"
                    onClick={openAssignModal}
                    data-testid="admin-player-report-assign-move-btn"
                  >
                    <ArrowUpRight className="w-3.5 h-3.5 mr-1.5" />
                    {team?.name ? 'Move to another team' : 'Assign to roster'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Parents + Address */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <Card className="lg:col-span-2" data-testid="admin-player-report-parents">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold text-navy flex items-center"><Users className="w-4 h-4 mr-2" /> Parent / Guardian</CardTitle>
              {data?.household?.household_id && (
                <button
                  onClick={() => setLinkGuardianOpen(true)}
                  className="text-xs font-semibold text-navy hover:underline"
                  data-testid="admin-player-report-add-guardian-btn"
                >
                  + {parent ? 'Add Parent / Guardian' : 'Link Parent / Guardian'}
                </button>
              )}
            </CardHeader>
            <CardContent>
              {parent ? (
                <ParentBlock p={parent} isPrimary />
              ) : (
                <p className="text-sm text-gray-500" data-testid="admin-player-report-no-parent">No parent/guardian linked</p>
              )}
              {secondary.length > 0 && (
                <div className="mt-4 pt-4 border-t" data-testid="admin-player-report-secondary-parents">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-semibold">Additional Guardian{secondary.length > 1 ? 's' : ''}</p>
                  <div className="space-y-3">
                    {secondary.map((sp, idx) => (<ParentBlock key={sp.parent_id || idx} p={sp} />))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="admin-player-report-address">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-navy flex items-center"><MapPin className="w-4 h-4 mr-2" /> Address</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700"><Em>{fullAddress}</Em></p>
            </CardContent>
          </Card>
        </div>

        {/* Medical Notes */}
        <Card className={`mb-4 ${player.medical_notes ? 'bg-amber-50 border-amber-200' : ''}`} data-testid="admin-player-report-medical">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-navy flex items-center"><HeartPulse className="w-4 h-4 mr-2 text-amber-700" /> Medical Notes / Allergies</CardTitle>
          </CardHeader>
          <CardContent>
            {player.medical_notes ? (
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{player.medical_notes}</p>
            ) : (
              <p className="text-sm text-gray-500">None on file</p>
            )}
          </CardContent>
        </Card>

        {/* Compliance */}
        <Card className="mb-6" data-testid="admin-player-report-compliance">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-navy flex items-center"><FileText className="w-4 h-4 mr-2" /> Registration &amp; Compliance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-navy">{waiversSigned}/{WAIVER_DEFS.length}</p>
                <p className="text-xs text-gray-500 mt-1">Waivers signed</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-navy">{docsUploaded}/{DOC_DEFS.length}</p>
                <p className="text-xs text-gray-500 mt-1">Documents uploaded</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-navy">{data.waiver_count || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Total signed</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-navy">{data.document_count || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Total uploaded</p>
              </div>
            </div>

            {missingItems.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4 flex items-start" data-testid="admin-player-report-missing">
                <AlertTriangle className="w-4 h-4 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold text-orange-900">{missingItems.length} item{missingItems.length > 1 ? 's' : ''} missing</p>
                  <ul className="mt-1 list-disc list-inside text-orange-800">
                    {missingItems.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">Required Waivers</p>
                <ul className="space-y-1.5">
                  {waiverRows.map(w => (
                    <li key={w.type} className="flex items-center justify-between text-sm" data-testid={`admin-player-report-waiver-${w.type}`}>
                      <div className="flex items-center gap-2 text-gray-700">
                        {w.signed ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                        <span>{w.label}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {w.signed
                          ? `Signed${w.signature ? ` · ${w.signature}` : ''}${w.signed_at ? ` · ${new Date(w.signed_at).toLocaleDateString()}` : ''}`
                          : <span className="text-red-600">Not signed</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">Uploaded Documents</p>
                <ul className="space-y-1.5">
                  {docRows.map(d => (
                    <li key={d.type} className="flex items-center justify-between text-sm" data-testid={`admin-player-report-doc-${d.type}`}>
                      <div className="flex items-center gap-2 text-gray-700">
                        {d.uploaded ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                        <span>{d.label}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {d.uploaded ? (d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : 'Uploaded') : <span className="text-red-600">Missing</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Super-admin Assign / Move modal */}
      {isSuperAdmin && (
        <Dialog open={assignOpen} onOpenChange={(open) => { if (!assignSubmitting) setAssignOpen(open); }}>
          <DialogContent className="max-w-lg" data-testid="admin-player-report-assign-modal">
            <DialogHeader>
              <DialogTitle className="text-navy flex items-center gap-2">
                <ArrowUpRight className="w-4 h-4" />
                {assignOptions?.player?.current_team_id ? 'Move player to another team' : 'Assign player to roster'}
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-500">
                Super-admin override. Same-division teams keep the player at their current age group.
                One-level-up teams require a reason and record a play-up override on the roster.
              </DialogDescription>
            </DialogHeader>

            {assignLoading ? (
              <div className="py-6 text-center text-sm text-gray-500">Loading options…</div>
            ) : (
              <div className="space-y-4">
                {assignOptions?.player && (
                  <div className="text-xs text-gray-600 bg-gray-50 border rounded-md p-2 space-y-0.5" data-testid="admin-player-report-assign-context">
                    <div>
                      <span className="text-gray-500">Stored age group:</span>{' '}
                      <span className="font-semibold">{assignOptions.player.stored_age_group || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Season age group (July 1, {assignOptions.season_year}):</span>{' '}
                      <span className="font-semibold">{assignOptions.player.season_age_group || '—'}</span>
                    </div>
                    <div data-testid="admin-player-report-true-division">
                      <span className="text-gray-500">True season division:</span>{' '}
                      <span className="font-semibold">
                        {assignOptions.player.player_division_id_true_name
                          || assignOptions.player.player_division_id_true
                          || '—'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Current team:</span>{' '}
                      <span className="font-semibold">{assignOptions.player.current_team_name || 'Unrostered'}</span>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1 font-semibold">Same division</p>
                  {(assignOptions?.same_division_teams || []).length === 0 ? (
                    <p className="text-xs text-gray-500 italic">No available same-division teams.</p>
                  ) : (
                    <div className="space-y-1" data-testid="admin-player-report-assign-same-division-list">
                      {assignOptions.same_division_teams.map(t => (
                        <label key={t.team_id} className="flex items-center gap-2 text-sm border rounded-md p-2 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="radio"
                            name="assign-team"
                            value={t.team_id}
                            checked={assignChoiceTeamId === t.team_id}
                            onChange={() => setAssignChoiceTeamId(t.team_id)}
                            data-testid={`admin-player-report-assign-option-${t.team_id}`}
                          />
                          <span>{t.team_name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1 font-semibold">
                    Play up one level {assignOptions?.play_up_target_division_id
                      ? `→ ${assignOptions.play_up_target_division_name || assignOptions.play_up_target_division_id}`
                      : ''}
                  </p>
                  {(assignOptions?.play_up_teams || []).length === 0 ? (
                    <p className="text-xs text-gray-500 italic">No play-up teams available.</p>
                  ) : (
                    <div className="space-y-1" data-testid="admin-player-report-assign-play-up-list">
                      {assignOptions.play_up_teams.map(t => (
                        <label key={t.team_id} className="flex items-center gap-2 text-sm border rounded-md p-2 hover:bg-amber-50 cursor-pointer">
                          <input
                            type="radio"
                            name="assign-team"
                            value={t.team_id}
                            checked={assignChoiceTeamId === t.team_id}
                            onChange={() => setAssignChoiceTeamId(t.team_id)}
                            data-testid={`admin-player-report-assign-option-${t.team_id}`}
                          />
                          <span>{t.team_name}</span>
                          <Badge className="ml-auto bg-amber-100 text-amber-800 text-[10px]">Play up</Badge>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <details className="border rounded-md" data-testid="admin-player-report-assign-all-divisions">
                  <summary className="cursor-pointer select-none px-2 py-1.5 text-xs uppercase tracking-wider text-gray-600 font-semibold hover:bg-gray-50">
                    All divisions (super-admin manual override)
                  </summary>
                  <div className="p-2 space-y-3 max-h-64 overflow-y-auto">
                    {(assignOptions?.all_teams_by_division || []).length === 0 ? (
                      <p className="text-xs text-gray-500 italic">No divisions in this program ladder.</p>
                    ) : (
                      (assignOptions.all_teams_by_division || []).map((g) => (
                        <div key={g.division_id} data-testid={`admin-player-report-assign-group-${g.division_id}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold text-gray-800">{g.division_name}</span>
                            {g.is_true_division && (
                              <Badge className="bg-green-100 text-green-800 text-[10px]">True season</Badge>
                            )}
                            {g.is_stored_division && !g.is_true_division && (
                              <Badge className="bg-gray-200 text-gray-700 text-[10px]">Stored</Badge>
                            )}
                            {g.is_play_up_target && (
                              <Badge className="bg-amber-100 text-amber-800 text-[10px]">Play up</Badge>
                            )}
                            {!g.is_true_division && !g.is_stored_division && !g.is_play_up_target && (
                              <Badge className="bg-red-100 text-red-800 text-[10px]">Manual override</Badge>
                            )}
                          </div>
                          <div className="space-y-1">
                            {g.teams.length === 0 ? (
                              <p className="text-[11px] text-gray-500 italic pl-2">No teams.</p>
                            ) : (
                              g.teams.map((t) => (
                                <label
                                  key={t.team_id}
                                  className={`flex items-center gap-2 text-sm border rounded-md p-2 ${
                                    t.is_current_team
                                      ? 'bg-gray-100 opacity-60 cursor-not-allowed'
                                      : t.requires_manual_override
                                        ? 'hover:bg-red-50 cursor-pointer border-red-100'
                                        : t.requires_play_up
                                          ? 'hover:bg-amber-50 cursor-pointer'
                                          : 'hover:bg-gray-50 cursor-pointer'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name="assign-team"
                                    value={t.team_id}
                                    disabled={t.is_current_team}
                                    checked={assignChoiceTeamId === t.team_id}
                                    onChange={() => { setAssignChoiceTeamId(t.team_id); setAssignReason(''); }}
                                    data-testid={`admin-player-report-assign-option-${t.team_id}`}
                                  />
                                  <span className={t.is_current_team ? 'line-through' : ''}>{t.team_name}</span>
                                  {t.is_current_team && (
                                    <span className="ml-auto text-[10px] text-gray-500 uppercase tracking-wider">Current</span>
                                  )}
                                </label>
                              ))
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </details>

                {(() => {
                  const chosen = [
                    ...(assignOptions?.same_division_teams || []),
                    ...(assignOptions?.play_up_teams || []),
                    ...((assignOptions?.all_teams_by_division || []).flatMap(g => g.teams || [])),
                  ].find(t => t.team_id === assignChoiceTeamId);
                  if (chosen?.requires_manual_override) {
                    return (
                      <div>
                        <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 font-semibold block">
                          Reason for manual override (required)
                        </label>
                        <textarea
                          value={assignReason}
                          onChange={(e) => setAssignReason(e.target.value)}
                          maxLength={200}
                          rows={3}
                          className="w-full text-sm border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-navy"
                          placeholder="Why is this player being manually moved outside the true / stored / play-up buckets?"
                          data-testid="admin-player-report-assign-override-reason"
                        />
                      </div>
                    );
                  }
                  if (chosen?.requires_play_up) {
                    return (
                      <div>
                        <label className="text-xs uppercase tracking-wider text-gray-500 mb-1 font-semibold block">
                          Reason for play-up (required)
                        </label>
                        <textarea
                          value={assignReason}
                          onChange={(e) => setAssignReason(e.target.value)}
                          maxLength={200}
                          rows={3}
                          className="w-full text-sm border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-navy"
                          placeholder="Why is this player playing up one level?"
                          data-testid="admin-player-report-assign-play-up-reason"
                        />
                      </div>
                    );
                  }
                  return null;
                })()}

                {assignError && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2" data-testid="admin-player-report-assign-error">
                    {assignError}
                  </div>
                )}
                {assignSuccess && (
                  <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-2" data-testid="admin-player-report-assign-success">
                    {assignSuccess}
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setAssignOpen(false)}
                disabled={assignSubmitting}
                data-testid="admin-player-report-assign-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={submitAssignment}
                disabled={assignLoading || assignSubmitting || !assignChoiceTeamId}
                className="bg-navy text-white hover:bg-navy/90"
                data-testid="admin-player-report-assign-submit-btn"
              >
                {assignSubmitting ? 'Saving…' : (assignOptions?.player?.current_team_id ? 'Move player' : 'Assign player')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {linkGuardianOpen && data?.household?.household_id && (
        <AdminLinkGuardianModal
          householdId={data.household.household_id}
          playerId={data.player?.player_id}
          playerName={`${data.player?.first_name || ''} ${data.player?.last_name || ''}`.trim()}
          onClose={() => setLinkGuardianOpen(false)}
          onSuccess={fetchReport}
        />
      )}
    </ParentLayout>
  );
}